import {
  type Dictionary,
  type Segment,
  segment,
  type Syllable,
  writeSyllable,
} from "@kensio/pinyinjs";

import { frequencyBucketOf } from "./word-frequency.js";

/**
 * Turning a text into the list of words it is made of.
 *
 * The tool's whole job, and the part with no DOM in it. `segment` hands back
 * every stretch of the text in order, including the stretches that were never
 * Han. What is left to do is decide which of those are words, gather the
 * repeats, and put the gathered list in an order a learner can use.
 */

/** One distinct word of a text, with everything the list shows about it. */
export interface SegmentedWord {
  /** The characters, in whichever script the text wrote them. */
  readonly text: string;

  /** The 普通话 reading the decoder settled on for this word. */
  readonly reading: readonly Syllable[];

  /** How many times the text uses it. */
  readonly count: number;

  /** Where it first appears, in code points from the start of the text. */
  readonly firstAt: number;

  /**
   * How common the word is in general, 0 to 15, or nothing where the
   * dictionary has no entry for it.
   */
  readonly frequency: number | undefined;

  /** jieba's tag for the first occurrence, or the empty string. */
  readonly partOfSpeech: string;

  readonly isProperNoun: boolean;
}

/** How the list can be ordered. */
export type WordOrder = "count" | "frequency" | "appearance" | "length";

/**
 * Whether a segment is a word this list should hold.
 *
 * `Segment.isKnown` is false for a stretch that was never Han, which covers
 * the spaces, the punctuation, the Latin and the digits. It is also false for
 * a Han character no source has a reading for, and those stay out too: a row
 * offering no reading and no frequency has nothing in it but the character.
 *
 * A word arriving with no reading is treated the same way for the same reason,
 * although `segment` gives every known Han word one.
 */
function isWord(found: Segment): boolean {
  return found.isKnown && found.reading.length > 0;
}

/**
 * What separates one row from another.
 *
 * **The writing and the reading together, and not the writing alone.** 得 is
 * `de` after a verb and `děi` before one, and a list collapsing those into one
 * row has published a word with two pronunciations and picked one. The 词典
 * nests senses inside readings for exactly this reason, and a word list that
 * disagreed with it would be the one page on the site that did.
 *
 * The reading is written out rather than compared syllable by syllable,
 * because a key has to be a string and `writeSyllable` is what the rest of the
 * site writes a syllable with.
 */
function keyOf(found: Segment): string {
  return `${found.text} ${found.reading.map((syllable) => writeSyllable(syllable)).join(" ")}`;
}

/**
 * Every distinct word of a text, in the order it first appears.
 *
 * Ordering is {@link orderedWords}'s. This one gathers, so that a caller
 * changing the order never re-segments and the sort is over a few hundred
 * objects rather than a few thousand.
 *
 * The frequency of each word is looked up once here, for the same reason.
 */
export function segmentedWords(
  dictionary: Dictionary,
  text: string,
): readonly SegmentedWord[] {
  const gathered = new Map<string, SegmentedWord>();

  for (const found of segment(dictionary, text)) {
    if (!isWord(found)) {
      continue;
    }

    const key = keyOf(found);
    const held = gathered.get(key);

    if (held === undefined) {
      gathered.set(key, {
        text: found.text,
        reading: found.reading,
        count: 1,
        firstAt: found.at,
        frequency: frequencyBucketOf(dictionary, found.text),
        partOfSpeech: found.partOfSpeech,
        isProperNoun: found.isProperNoun,
      });
    } else {
      gathered.set(key, { ...held, count: held.count + 1 });
    }
  }

  return [...gathered.values()];
}

/**
 * How many characters a word is written with.
 *
 * Code points rather than UTF-16 units, which is the same unit `Segment.at`
 * counts in and the same unit `characters` in `src/content/chinese/hanzi-slug.ts`
 * settled on for the same reason. 𠮷 is a surname and one character.
 *
 * The lint rule turned off here is about grapheme clusters and is right in
 * general. It cannot reach this: the input is a word the dictionary holds a
 * key for, so there are no emoji and no combining accents in it.
 */
function characterCount(word: string): number {
  // oxlint-disable-next-line typescript/no-misused-spread -- code points are the unit, deliberately: see above
  return [...word].length;
}

/**
 * The comparison each order sorts by, before ties are broken.
 *
 * Each returns a negative number where the first word comes first, which is
 * what `toSorted` wants. Frequency descends, since a reader asking to see the
 * commonest words means the commonest first, and a word with no entry sorts to
 * the bottom rather than the top.
 */
const comparisons: Readonly<
  Record<WordOrder, (one: SegmentedWord, other: SegmentedWord) => number>
> = {
  count: (one, other) => other.count - one.count,
  frequency: (one, other) => (other.frequency ?? -1) - (one.frequency ?? -1),
  appearance: (one, other) => one.firstAt - other.firstAt,
  length: (one, other) => characterCount(other.text) - characterCount(one.text),
};

/**
 * The list in one of its orders, with every tie broken the same way.
 *
 * **Ties are the common case rather than an edge.** Most words in a text
 * appear once, so ordering by count leaves nearly the whole list tied, and the
 * sixteen frequency levels resolve to eleven over the 264 distinct words in
 * 1,000 characters. An unbroken tie is the order a `Map` happened to hold,
 * which shifts under an edit somewhere else in the text and makes the list
 * look like it is shuffling itself.
 *
 * So a tie falls back to how often the text uses the word, and then to where
 * it first appeared, which is unique. Every order is therefore total, and a
 * word keeps its place while the rest of the text changes around it.
 */
export function orderedWords(
  words: readonly SegmentedWord[],
  order: WordOrder,
): readonly SegmentedWord[] {
  const compare = comparisons[order];

  return words.toSorted(
    (one, other) =>
      compare(one, other) ||
      other.count - one.count ||
      one.firstAt - other.firstAt,
  );
}

/** How many words the text holds in total, counting every repeat. */
export function totalWords(words: readonly SegmentedWord[]): number {
  return words.reduce((running, word) => running + word.count, 0);
}
